import * as React from 'react';
import { Route } from 'react-router-dom';
import AssetDataOverviewHOC from '../../shared/dataSharing/AssetDataOverview';
import AssetDataDetailsHOC from '../../shared/dataSharing/AssetDataDetails';
import DataSharingService from '../../shared/services/DataSharingService';
import { Status } from '../../shared/constants';

const Label = {
  Status: {
    [Status.New]: 'New data user',
    [Status.Requested]: 'New request',
    [Status.RequestDeclined]: 'Request declined',
    [Status.Offered]: 'Offer sent',
    [Status.OfferDeclined]: 'Offer declined',
    [Status.Accepted]: 'Active',
    [Status.Expiring]: 'Expiring',
    [Status.ExpiringReplaced]: 'Expiring replaced',
    [Status.Scheduled]: 'Scheduled',
    [Status.Suspended]: 'Stopped',
    [Status.SuspendedReplaced]: 'Stopped replaced'
  },
  Action: {
    [Status.New]: 'Reply',
    [Status.Requested]: 'Reply',
    [Status.RequestDeclined]: 'View details',
    [Status.Offered]: 'View details',
    [Status.OfferDeclined]: 'Reply',
    [Status.Accepted]: 'Reply',
    [Status.Expiring]: 'Reply',
    [Status.ExpiringReplaced]: 'View details',
    [Status.Scheduled]: 'View details',
    [Status.Suspended]: 'Reply',
    [Status.SuspendedReplaced]: 'View details',
  },
  Entity: {
    Name: 'data user',
  },
};

const DataProperty = {
  Entity: 'subscription',
};

const OverviewConfiguration = {
  GetAgreements: DataSharingService.GetDataProviderAgreements,
};

const DetailsConfiguration = {
  [Status.New]: {
    sendRemark: true,
    sendFees: true,
    sendDurationMonths: true,
    actions: {
      sendOffer: {
        label: 'Send offer',
        successMessage: 'Successfully sent offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Offered,
            state.agreement.currency,
            state.remark,
            state.fees,
            state.durationMonths,
          );
        },
      },
    },
  },
  [Status.Requested]: {
    sendRemark: true,
    sendFees: true,
    sendDurationMonths: true,
    actions: {
      declineRequest: {
        label: 'Decline request',
        successMessage: 'Successfully declined request.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.RequestDeclined,
            state.agreement.currency,
            state.remark
          );
        },
      },
      replyWithOffer: {
        label: 'Reply with offer',
        successMessage: 'Successfully replied with offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Offered,
            state.agreement.currency,
            state.remark,
            state.fees,
            state.durationMonths,
          );
        },
      },
    },
  },
  [Status.RequestDeclined]: {
    sendRemark: false,
    sendFees: false,
    sendDurationMonths: false,
    actions: {},
  },
  [Status.Offered]: {
    sendRemark: false,
    sendFees: false,
    sendDurationMonths: false,
    actions: {},
  },
  [Status.OfferDeclined]: {
    sendRemark: true,
    sendFees: true,
    sendDurationMonths: true,
    actions: {
      sendNewOffer: {
        label: 'Send new offer',
        successMessage: 'Successfully sent new offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Offered,
            state.agreement.currency,
            state.remark,
            state.fees,
            state.durationMonths,
          );
        },
      },
    },
  },
  [Status.Accepted]: {
    sendRemark: true,
    sendFees: true,
    sendDurationMonths: true,
    actions: {
      stop: {
        label: 'Stop agreement',
        successMessage: 'Successfully expired offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Expiring,
            state.agreement.currency,
            state.remark,
          );
        },
        areYouSure: 'This action will expire your active agreement.',
      },
      renegotiateOffer: {
        label: 'Renegotiate offer',
        successMessage: 'Successfully sent new offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Offered,
            state.agreement.currency,
            state.remark,
            state.fees,
            state.durationMonths
          );
        },
        areYouSure: 'This action will expire your active agreement.',
      },
    },
  },
  [Status.Expiring]: {
    sendRemark: true,
    sendFees: true,
    sendDurationMonths: true,
    actions: {
      renegotiateOffer: {
        label: 'Renegotiate offer',
        successMessage: 'Successfully sent new offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Offered,
            state.agreement.currency,
            state.remark,
            state.fees,
            state.durationMonths
          );
        },
      },
    },
  },
  [Status.ExpiringReplaced]: {
    sendRemark: false,
    sendFees: false,
    sendDurationMonths: false,
    actions: {},
  },
  [Status.Scheduled]: {
    sendRemark: false,
    sendFees: false,
    sendDurationMonths: false,
    actions: {},
  },
  [Status.Suspended]: {
    sendRemark: true,
    sendFees: true,
    sendDurationMonths: true,
    actions: {
      renegotiateOffer: {
        label: 'Renegotiate offer',
        successMessage: 'Successfully sent new offer.',
        onAction: (state: any) => {
          return DataSharingService.PostAgreementStateChange(
            state.agreement.id,
            Status.Offered,
            state.agreement.currency,
            state.remark,
            state.fees,
            state.durationMonths
          );
        },
      },
    },
  },
  [Status.SuspendedReplaced]: {
    sendRemark: false,
    sendFees: false,
    sendDurationMonths: false,
    actions: {},
  },
};

const ProviderAssetDataOverview = AssetDataOverviewHOC(Label, DataProperty, OverviewConfiguration);
const ProviderAssetDataDetails = AssetDataDetailsHOC(Label, DataProperty, DetailsConfiguration);

class ProviderAssetData extends React.Component<any, any> {
  public get basePath() {
    const { assetId } = this.props;
    return `/providerassets/${assetId}/data`;
  }

  public getProviderAssetDataOverviewWithProps = (routeProps: any) => {
    return <ProviderAssetDataOverview {...routeProps} {...this.props} basePath={this.basePath} />;
  };

  public getProviderAssetDataDetailsWithProps = (routeProps: any) => {
    return <ProviderAssetDataDetails {...routeProps} {...this.props} />
  }

  public render() {
    return (
      <>
        <Route path={this.basePath} exact render={this.getProviderAssetDataOverviewWithProps} />
        <Route path={this.basePath + '/:agreementId'} exact render={this.getProviderAssetDataDetailsWithProps} />
      </>
    );
  }
}

export default ProviderAssetData;



// WEBPACK FOOTER //
// ./src/modules/provider/pages/ProviderAssetData.tsx